import { useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import {
  Alert,
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import { EMR_LOGIN_URLS, NOTE_TYPES } from '../constants/EmrNoteTypeMapping';

const EMPTY_FORM = {
  targetType: 'group',
  target: '',
  noteType: '',
  section: '',
  subsection: '',
  instruction: '',
  replyType: '',
};

const REPLY_TYPES = ['overwrite', 'append', 'no_reply'];

const emrUrlSubstring = (url) => {
  try {
    return new URL(url).hostname.split('.').slice(-2).join('.');
  } catch {
    return url;
  }
};

const EMR_OPTIONS = Object.entries(EMR_LOGIN_URLS)
  .map(([name, url]) => ({ name, urlSubstring: emrUrlSubstring(url) }))
  .sort((a, b) => a.name.localeCompare(b.name));

const BASE_NOTE_TYPES = NOTE_TYPES.map((n) => n.name);
const toOptions = (values) => values.map((v) => ({ value: v, label: v }));
const toValue = (v) => (v ? { value: v, label: v } : null);

const truncate = (v, n = 140) => {
  const s = typeof v === 'string' ? v : JSON.stringify(v ?? '');
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

// COM-490: replace one instruction (note type → section → optional View More
// subsection, and/or reply type) for a whole group sub or a single user.
const InstructionPush = ({ apiClient }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emrName, setEmrName] = useState('');
  const [noteTypes, setNoteTypes] = useState(BASE_NOTE_TYPES);
  const [sections, setSections] = useState([]);
  const [subsectionsBySection, setSubsectionsBySection] = useState({});

  const setField = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setPreview(null);
  };
  const update = (key) => (e) => setField({ [key]: e.target.value });

  // Same option source as the EMR Directives dashboard.
  const selectEmr = async (name) => {
    setEmrName(name);
    setField({ noteType: '', section: '', subsection: '' });
    const emr = EMR_OPTIONS.find((o) => o.name === name);
    let res = null;
    if (emr) {
      try {
        res = await apiClient.getEMRDirectiveOptions(
          emr.name,
          emr.urlSubstring,
        );
      } catch {
        res = null;
      }
    }
    setNoteTypes(
      [...new Set([...BASE_NOTE_TYPES, ...(res?.note_types || [])])].sort(),
    );
    setSections(res?.sections || []);
    setSubsectionsBySection(res?.subsections_by_section || {});
  };

  const request = () => ({
    [form.targetType === 'group' ? 'groupId' : 'email']: form.target.trim(),
    noteType: form.noteType.trim(),
    section: form.section.trim(),
    subsection: form.subsection.trim() || undefined,
    instruction: form.instruction.trim() ? form.instruction : undefined,
    replyType: form.replyType || undefined,
  });

  const run = async (fn) => {
    setBusy(true);
    setStatus(null);
    try {
      const rsp = await fn();
      if (rsp?.error) setStatus({ error: true, text: rsp.error });
      return rsp?.error ? null : rsp;
    } catch (err) {
      setStatus({ error: true, text: String(err) });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    const rsp = await run(() =>
      apiClient.adminPreviewInstructionPush(request()),
    );
    if (rsp) setPreview(rsp);
  };

  const apply = async () => {
    const n = preview?.summary?.configs_to_update || 0;
    if (!window.confirm(`Overwrite this instruction in ${n} template(s)?`))
      return;
    const rsp = await run(() =>
      apiClient.adminApplyInstructionPush(request(), mfaCode.trim()),
    );
    setMfaCode('');
    if (rsp) {
      const failed = rsp.failed?.length ? `, ${rsp.failed.length} failed` : '';
      setStatus({ error: false, text: `Updated ${rsp.updated}${failed}.` });
      setPreview(null);
    }
  };

  const rows = (preview?.users || []).flatMap((u) =>
    u.configs.length
      ? u.configs.map((c) => ({ email: u.email, ...c }))
      : [{ email: u.email, skip_reason: 'No templates for this note type' }],
  );
  const canPreview =
    form.target.trim() &&
    form.noteType.trim() &&
    form.section.trim() &&
    (form.instruction.trim() || form.replyType);

  return (
    <div>
      <Alert color="warning">
        Overwrites the selected instruction in every matching template. Preview
        first; applying requires your MFA code.
      </Alert>
      <Row>
        <Col md={3}>
          <FormGroup>
            <Label>Push to</Label>
            <Input
              type="select"
              value={form.targetType}
              onChange={update('targetType')}
            >
              <option value="group">Group subscription ID</option>
              <option value="user">User email(s)</option>
            </Input>
          </FormGroup>
        </Col>
        <Col md={5}>
          <FormGroup>
            <Label>
              {form.targetType === 'group'
                ? 'Group subscription ID'
                : 'User emails (comma separated)'}
            </Label>
            <Input value={form.target} onChange={update('target')} />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>EMR (loads dropdown options)</Label>
            <Input
              type="select"
              value={emrName}
              onChange={(e) => selectEmr(e.target.value)}
            >
              <option value="">— Select EMR —</option>
              {EMR_OPTIONS.map((o) => (
                <option key={o.name} value={o.name}>
                  {o.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Note type</Label>
            <CreatableSelect
              isClearable
              placeholder="e.g. Daily Note, Evaluation"
              options={toOptions(noteTypes)}
              value={toValue(form.noteType)}
              onChange={(opt) => setField({ noteType: opt ? opt.value : '' })}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Section</Label>
            <CreatableSelect
              isClearable
              placeholder="e.g. TREATMENTS, ROM"
              options={toOptions(sections)}
              value={toValue(form.section)}
              onChange={(opt) =>
                setField({ section: opt ? opt.value : '', subsection: '' })
              }
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>View More subsection (optional)</Label>
            <CreatableSelect
              isClearable
              isDisabled={!form.section.trim()}
              placeholder="e.g. Pain Level, ROM"
              options={toOptions(subsectionsBySection[form.section] || [])}
              value={toValue(form.subsection)}
              onChange={(opt) => setField({ subsection: opt ? opt.value : '' })}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Reply type</Label>
            <Input
              type="select"
              value={form.replyType}
              onChange={update('replyType')}
            >
              <option value="">Leave unchanged</option>
              {REPLY_TYPES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>
      <FormGroup>
        <Label>New instruction (leave blank to change reply type only)</Label>
        <Input
          type="textarea"
          rows={6}
          value={form.instruction}
          onChange={update('instruction')}
        />
      </FormGroup>
      <Button
        color="primary"
        disabled={busy || !canPreview}
        onClick={runPreview}
      >
        Preview
      </Button>

      {status && (
        <Alert className="mt-3" color={status.error ? 'danger' : 'success'}>
          {status.text}
        </Alert>
      )}

      {preview && (
        <div className="mt-4">
          <h5>
            {preview.summary.configs_to_update} template(s) to update across{' '}
            {preview.summary.users} user(s)
          </h5>
          <Table size="sm" responsive>
            <thead>
              <tr>
                <th>User</th>
                <th>Template</th>
                <th>Status</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.email}-${r.config_id || i}`}>
                  <td>{r.email}</td>
                  <td>{r.name}</td>
                  <td>
                    {r.skip_reason ? (
                      <Badge color="secondary">{r.skip_reason}</Badge>
                    ) : (
                      <Badge color="success">Will update</Badge>
                    )}
                  </td>
                  <td>
                    {r.before &&
                      `[${r.before.reply_type}] ${truncate(r.before.instruction)}`}
                  </td>
                  <td>
                    {r.after &&
                      `[${r.after.reply_type}] ${truncate(r.after.instruction)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="align-items-end">
            <Col md={3}>
              <FormGroup>
                <Label>MFA code</Label>
                <Input
                  value={mfaCode}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={(e) => setMfaCode(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Button
                  color="danger"
                  disabled={
                    busy ||
                    !mfaCode.trim() ||
                    !preview.summary.configs_to_update
                  }
                  onClick={apply}
                >
                  Apply push
                </Button>
              </FormGroup>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default InstructionPush;
